import moment from 'moment';

import {
  CasesPerDayRow,
  groupSpans,
  OrdersByCustomerRow,
  TotalCasesRow,
} from './ordersSummary';

const ORDERS_BY_CUSTOMER_COLS = 6;
const CASES_PER_DAY_COLS = 5;
const TOTAL_CASES_COLS = 4;

const TABLE_STYLES = `
  .title { font-weight: bold; font-size: 18pt; }
  .spacer { width: 18px; border: none; }
  table { border-collapse: collapse; font-family: Calibri, Arial, sans-serif; width: 100%; }
  th, td { border: 1px solid #bfbfbf; padding: 4px 8px; vertical-align: middle; font-size: 10pt; }
  th { font-weight: bold; background: #e2f0d9; text-align: left; }
`;

type SummaryTables = {
  ordersByCustomer: OrdersByCustomerRow[];
  casesPerDay: CasesPerDayRow[];
  totalCases: TotalCasesRow[];
};

const escapeHtml = (value: string | number): string =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const emptyCells = (count: number): string =>
  Array.from({ length: count }, () => '<td></td>').join('');

const headerRow = (labels: string[]): string =>
  labels.map((label) => `<th>${escapeHtml(label)}</th>`).join('');

const titleCell = (title: string, colspan: number): string =>
  `<td class="title" colspan="${colspan}">${escapeHtml(title)}</td>`;

const spacer = '<td class="spacer"></td>';

const dataCell = (value: string | number): string =>
  `<td>${escapeHtml(value)}</td>`;

const mergeCell = (value: string | number, rowspan: number): string =>
  rowspan > 1
    ? `<td rowspan="${rowspan}">${escapeHtml(value)}</td>`
    : dataCell(value);

const readyMissing = `${dataCell('')}${dataCell('')}`;

const ordersByCustomerRowHtml = (
  row: OrdersByCustomerRow | undefined,
  index: number,
  dateSpans: number[],
  customerSpans: number[],
): string => {
  if (!row) {
    return emptyCells(ORDERS_BY_CUSTOMER_COLS);
  }

  const cells: string[] = [];

  if (dateSpans[index]) {
    cells.push(mergeCell(row.deliveryDate, dateSpans[index]));
  }

  if (customerSpans[index]) {
    cells.push(mergeCell(row.customer, customerSpans[index]));
  }

  cells.push(dataCell(row.packages));
  cells.push(dataCell(row.count));
  cells.push(readyMissing);

  return cells.join('');
};

const casesPerDayRowHtml = (
  row: CasesPerDayRow | undefined,
  index: number,
  dateSpans: number[],
): string => {
  if (!row) {
    return emptyCells(CASES_PER_DAY_COLS);
  }

  const cells: string[] = [];

  if (dateSpans[index]) {
    cells.push(mergeCell(row.deliveryDate, dateSpans[index]));
  }

  cells.push(dataCell(row.packages));
  cells.push(dataCell(row.count));
  cells.push(readyMissing);

  return cells.join('');
};

const totalCasesRowHtml = (row: TotalCasesRow | undefined): string => {
  if (!row) {
    return emptyCells(TOTAL_CASES_COLS);
  }

  return `${dataCell(row.packages)}${dataCell(row.count)}${dataCell(
    row.ready,
  )}${dataCell('')}`;
};

export const formatPrintTimestamp = (printedAt: Date = new Date()): string =>
  moment(printedAt).format('MMM D, YYYY h:mma');

export const formatPrintDate = (date: Date): string =>
  moment(date).format('MMM D, YYYY');

export const buildOrdersSummaryTablesHtml = ({
  ordersByCustomer,
  casesPerDay,
  totalCases,
}: SummaryTables): string => {
  const dateSpansByCustomer = groupSpans(ordersByCustomer, (row) => row.dayKey);
  const customerSpans = groupSpans(
    ordersByCustomer,
    (row) => `${row.dayKey}|${row.customer}`,
  );
  const dateSpansPerDay = groupSpans(casesPerDay, (row) => row.dayKey);

  const bodyRows = Math.max(
    ordersByCustomer.length,
    casesPerDay.length,
    totalCases.length,
  );

  const rows: string[] = [
    `<tr>${titleCell(
      'Orders By Customer',
      ORDERS_BY_CUSTOMER_COLS,
    )}${spacer}${titleCell(
      'Cases Per Day',
      CASES_PER_DAY_COLS,
    )}${spacer}${titleCell('Total Cases', TOTAL_CASES_COLS)}</tr>`,
    `<tr>${headerRow([
      'Delivery Date',
      'Customer',
      'Packages',
      'Count',
      'Ready',
      'Missing',
    ])}${spacer}${headerRow([
      'Delivery Date',
      'Packages',
      'Count',
      'Ready',
      'Missing',
    ])}${spacer}${headerRow(['Packages', 'Count', 'Ready', 'Missing'])}</tr>`,
  ];

  for (let index = 0; index < bodyRows; index += 1) {
    rows.push(
      `<tr>${ordersByCustomerRowHtml(
        ordersByCustomer[index],
        index,
        dateSpansByCustomer,
        customerSpans,
      )}${spacer}${casesPerDayRowHtml(
        casesPerDay[index],
        index,
        dateSpansPerDay,
      )}${spacer}${totalCasesRowHtml(totalCases[index])}</tr>`,
    );
  }

  return `<table>
${rows.join('\n')}
</table>`;
};

export const buildOrdersSummaryExcel = (tables: SummaryTables): string =>
  `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
<head>
<meta charset="UTF-8" />
<style>${TABLE_STYLES}</style>
</head>
<body>
${buildOrdersSummaryTablesHtml(tables)}
</body>
</html>`;

export const buildOrdersSummaryPrintHtml = ({
  hubName,
  startDate,
  endDate,
  printedAt = new Date(),
  ...tables
}: SummaryTables & {
  hubName: string;
  startDate: Date;
  endDate: Date;
  printedAt?: Date;
}): string => {
  const title = `Order Summary - ${hubName} ${formatPrintDate(
    startDate,
  )} to ${formatPrintDate(endDate)}`;
  const printedAtLabel = `Printed out at ${formatPrintTimestamp(printedAt)}`;

  return `<html>
<head>
<meta charset="UTF-8" />
<title>${escapeHtml(title)}</title>
<style>
  @page { size: landscape; margin: 12mm; }
  html, body { margin: 0; padding: 0; }
  body { font-family: Calibri, Arial, sans-serif; color: #222; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; margin-bottom: 16px; }
  .doc-title { font-size: 16pt; font-weight: bold; text-align: left; }
  .printed-at { font-size: 10pt; white-space: nowrap; text-align: right; }
  ${TABLE_STYLES}
</style>
</head>
<body>
  <div class="header">
    <div class="doc-title">${escapeHtml(title)}</div>
    <div class="printed-at">${escapeHtml(printedAtLabel)}</div>
  </div>
  ${buildOrdersSummaryTablesHtml(tables)}
</body>
</html>`;
};

export const downloadOrdersSummaryExcel = ({
  fileName,
  ...tables
}: SummaryTables & { fileName: string }): void => {
  const html = buildOrdersSummaryExcel(tables);
  const blob = new Blob([html], { type: 'application/vnd.ms-excel' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const printOrdersSummary = (
  options: SummaryTables & {
    hubName: string;
    startDate: Date;
    endDate: Date;
    printedAt?: Date;
  },
): void => {
  const html = buildOrdersSummaryPrintHtml(options);
  const iframe = document.createElement('iframe');

  iframe.setAttribute(
    'style',
    'position:fixed;right:0;bottom:0;width:0;height:0;border:0;',
  );
  document.body.appendChild(iframe);

  const frameWindow = iframe.contentWindow;
  const frameDocument = frameWindow?.document;

  if (!frameWindow || !frameDocument) {
    document.body.removeChild(iframe);
    return;
  }

  frameDocument.open();
  frameDocument.write(html);
  frameDocument.close();

  const cleanup = () => {
    if (iframe.parentNode) {
      document.body.removeChild(iframe);
    }
  };

  frameWindow.focus();
  setTimeout(() => {
    frameWindow.print();
    setTimeout(cleanup, 500);
  }, 250);
};
